import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, BarChart3, AlertTriangle, CalendarClock } from "lucide-react";

const COLORS = {
  Scheduled: "text-emerald-700", Published: "text-[#166534]", Sending: "text-indigo-700",
  Failed: "text-red-700", "Export Required": "text-amber-700", Canceled: "text-gray-400",
};

export default function PublishingReport() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/marketing/publishing/report/summary").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (data === false) return <p className="text-sm text-red-600">Failed to load report.</p>;

  const statuses = ["Scheduled", "Sending", "Published", "Failed", "Export Required"];
  return (
    <div data-testid="publishing-report-page">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#581C87]" /> Publishing Report</h2>
      <p className="text-sm text-gray-500 mt-1">Real database/provider data only. No views, reach or revenue are shown.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="report-counts">
        {statuses.map((s) => (
          <div key={s} className="bg-white border border-gray-200 rounded-xl p-4"><p className={`font-heading text-2xl font-bold ${COLORS[s] || ""}`}>{data.counts?.[s] || 0}</p><p className="text-xs text-gray-500">{s}</p></div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="by-platform">
          <h3 className="font-heading font-bold text-sm">By platform</h3>
          <div className="mt-3 space-y-2">
            {Object.entries(data.by_platform || {}).map(([p, sc]) => (
              <div key={p} className="text-sm flex justify-between border-b border-gray-50 py-1.5"><span>{p}</span><span className="text-gray-500 text-xs">{Object.entries(sc).map(([k, v]) => `${k}:${v}`).join("  ")}</span></div>
            ))}
            {Object.keys(data.by_platform || {}).length === 0 && <p className="text-sm text-gray-400">No publishing activity yet.</p>}
          </div>
          <p className="text-xs text-gray-400 mt-3">Failure rate: {data.failure_rate}%</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="upcoming">
          <h3 className="font-heading font-bold text-sm flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Upcoming 7 days</h3>
          <div className="mt-3 space-y-1">
            {(data.upcoming_7d || []).slice(0, 12).map((s) => (
              <div key={s.id} className="text-xs flex justify-between border-b border-gray-50 py-1"><span className="truncate">{s.platform}: {s.hook}</span><span className="text-gray-400">{(s.scheduled_at || "").slice(0, 16).replace("T", " ")}</span></div>
            ))}
            {(data.upcoming_7d || []).length === 0 && <p className="text-sm text-gray-400">Nothing scheduled in the next 7 days.</p>}
          </div>
        </div>
      </div>

      {(data.action_required || []).length > 0 && (
        <div className="mt-6 bg-white border border-amber-200 rounded-xl p-5" data-testid="report-actions">
          <h3 className="font-heading font-bold text-sm flex items-center gap-2 text-amber-700"><AlertTriangle className="h-4 w-4" /> Items requiring action</h3>
          <div className="mt-3 space-y-1">
            {data.action_required.map((p) => (
              <div key={p.id} className="text-xs flex justify-between border-b border-gray-50 py-1"><span>{p.platform} · {p.status}</span><span className="text-red-600">{p.failure_reason || "Export package ready"}</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
